import { create } from "zustand";
import type { Family, Kid, Room, Challenge, Completion, Cashout, Provider, StoreItem, Wish, Redemption, Task, TaskAssignment, FamilyMember } from "@starquest/core";

interface FamilyState {
  family:      Family | null;
  members:     FamilyMember[];
  kids:        Kid[];
  rooms:       Room[];
  challenges:  Challenge[];
  completions: Completion[];
  cashouts:    Cashout[];
  selectedKid: Kid | null;
  // Store
  providers:       Provider[];
  storeItems:      StoreItem[];
  wishes:          Wish[];
  redemptions:     Redemption[];
  wishlistItemIds: string[];   // itemIds hearted by selectedKid
  // Tasks
  taskAssignments: TaskAssignment[];
  customTasks:     Task[];
  setTaskAssignments: (a: TaskAssignment[]) => void;
  setCustomTasks:     (t: Task[]) => void;

  setFamily:          (f: Family) => void;
  setMembers:         (m: FamilyMember[]) => void;
  setKids:            (k: Kid[]) => void;
  setRooms:           (r: Room[]) => void;
  setChallenges:       (c: Challenge[]) => void;
  setCompletions:      (c: Completion[]) => void;
  setCashouts:        (c: Cashout[]) => void;
  selectKid:          (k: Kid | null) => void;
  updateKid:          (kidId: string, updates: Partial<Kid>) => void;
  // Store setters
  setProviders:       (p: Provider[]) => void;
  setStoreItems:      (i: StoreItem[]) => void;
  setWishes:          (w: Wish[]) => void;
  setRedemptions:     (r: Redemption[]) => void;
  setWishlistItemIds: (ids: string[]) => void;
}

export const useFamilyStore = create<FamilyState>((set) => ({
  family:      null,
  members:     [],
  kids:        [],
  rooms:       [],
  challenges:  [],
  completions: [],
  cashouts:    [],
  selectedKid: null,
  providers:       [],
  storeItems:      [],
  wishes:          [],
  redemptions:     [],
  wishlistItemIds: [],
  taskAssignments: [],
  customTasks:     [],

  setFamily:      (family)      => set({ family }),
  setMembers:     (members)     => set({ members }),
  setKids: (kids) => set((state) => ({
    kids,
    selectedKid: state.selectedKid
      ? (kids.find((k) => k.kidId === state.selectedKid!.kidId) ?? state.selectedKid)
      : null,
  })),
  setRooms:       (rooms)       => set({ rooms }),
  setChallenges:  (challenges)  => set({ challenges }),
  setCompletions: (completions) => set({ completions }),
  setCashouts:    (cashouts)    => set({ cashouts }),
  selectKid:      (selectedKid) => set({ selectedKid }),

  updateKid: (kidId, updates) =>
    set((state) => ({
      kids: state.kids.map((k) => (k.kidId === kidId ? { ...k, ...updates } : k)),
      selectedKid: state.selectedKid?.kidId === kidId
        ? { ...state.selectedKid, ...updates }
        : state.selectedKid,
    })),

  setProviders:       (providers)       => set({ providers }),
  setStoreItems:      (storeItems)      => set({ storeItems }),
  setWishes:          (wishes)          => set({ wishes }),
  setRedemptions:     (redemptions)     => set({ redemptions }),
  setWishlistItemIds: (wishlistItemIds) => set({ wishlistItemIds }),
  setTaskAssignments: (taskAssignments) => set({ taskAssignments }),
  setCustomTasks:     (customTasks)     => set({ customTasks }),
}));
